// Kosten eines Mandanten: der Plattform-Key bei Requesty, der eigene Key der
// Flow-Modelle (eigenes Limit, docs/11 8d) und die Summe der Instanz
// (`cost_usd` der Turns) im Monat aus `?month=` (Vorgabe: dieser).

import { useQuery } from "@tanstack/react-query";
import { get } from "../../api/client";
import { keys } from "../../api/queries";
import type { UsageResponse } from "../../api/types";
import type { ReactNode } from "react";
import { currentMonth, fmtDay, fmtInt, fmtUsd } from "../../lib/format";
import { errorText } from "../../lib/labels";
import { useSearchParams } from "../../router/router";
import { Meter, Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import type { TabProps } from "./tabs";

export function CostsTab({ tenant: t }: TabProps) {
  const month = useSearchParams().get("month") ?? currentMonth();
  const q = useQuery({
    queryKey: keys.tenantUsage(t.id, month),
    queryFn: () => get<UsageResponse>(`/tenants/${t.id}/usage?month=${encodeURIComponent(month)}`),
  });
  if (q.isPending) return <Card title="Kosten"><Note>Wird geladen …</Note></Card>;
  if (q.isError) return <Card title="Kosten"><Note tone="error">{errorText(q.error)}</Note></Card>;
  const res = q.data;
  const i = res.instance;
  const flows = res.flow_key || res.flow_key_error || t.models.flows.enabled;
  return (
    <>
      <Card
        title="Plattform-Key (Requesty)"
        hint="Ausgaben bei Requesty im laufenden Monat. Limit erreicht = harte Sperre — Modellaufrufe des Mandanten scheitern dann bis zum Monatsersten."
      >
        <KeyBudget info={res.platform_key} error={res.platform_key_error} none="Kein Plattform-Key." />
      </Card>
      {flows ? (
        <Card
          title="Flows — eigener Key (Requesty)"
          hint="Nur die Flow-Modelle (KI- und Entscheidungsschritte der Flows), mit eigenem Monatslimit. Erreicht: Die Flows stehen, Chats und Agenten laufen weiter."
        >
          <KeyBudget info={res.flow_key} error={res.flow_key_error} none="Noch kein Flow-Key — er entsteht beim nächsten Übernehmen." />
        </Card>
      ) : null}
      <Card title={`Instanz — ${res.month}`} hint="Summe aus cost_usd der Turns (GET /usage). Modelle ohne Preis fehlen in der Summe." flush>
        {i ? (
          <>
            <div className="pf-card__body">
              <div className="pf-stats"><div className="pf-stat"><span className="pf-stat__label">Summe</span><span className="pf-stat__value">{fmtUsd(i.total_usd)}</span></div></div>
              {i.unpriced_models.length ? <Note tone="warn">Ohne Preis: {i.unpriced_models.join(", ")}</Note> : null}
            </div>
            <div className="pf-table-wrap">
              <table className="pf-table">
                <thead><tr><th>Modell</th><th className="pf-num">Turns</th><th className="pf-num">Eingabe</th><th className="pf-num">Ausgabe</th><th className="pf-num">Kosten</th></tr></thead>
                <tbody>
                  {i.by_model.length ? i.by_model.map((m) => (
                    <tr key={m.model}><td>{m.model}</td><td className="pf-num">{fmtInt(m.turns)}</td><td className="pf-num">{fmtInt(m.input_tokens)}</td><td className="pf-num">{fmtInt(m.output_tokens)}</td><td className="pf-num">{fmtUsd(m.cost_usd)}</td></tr>
                  )) : <tr><td colSpan={5} className="pf-empty">Keine Turns</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        ) : <div className="pf-card__body"><Note tone="error">Instanz nicht erreichbar ({res.instance_error ?? ""})</Note></div>}
      </Card>
    </>
  );
}

/** Ausgaben, Limit und Monatsanfang eines Requesty-Keys, ab 80 % mit Warnung. */
function KeyBudget({ info: k, error, none }: { info: UsageResponse["platform_key"]; error?: string; none: ReactNode }) {
  if (!k) return <Note>{error ? error : none}</Note>;
  const pct = k.max_budget ? Math.min(100, (k.spend / k.max_budget) * 100) : null;
  return (
    <>
      <div className="pf-stats">
        <div className="pf-stat"><span className="pf-stat__label">Ausgegeben</span><span className="pf-stat__value">{fmtUsd(k.spend)}</span></div>
        <div className="pf-stat"><span className="pf-stat__label">Budget</span><span className="pf-stat__value">{fmtUsd(k.max_budget)}</span></div>
        <div className="pf-stat"><span className="pf-stat__label">Zurückgesetzt</span><span className="pf-stat__value">{fmtDay(k.budget_reset_at)}</span></div>
      </div>
      {pct !== null && <Meter pct={pct} tone={pct >= 95 ? "error" : pct >= 80 ? "warn" : ""} />}
      {pct !== null && pct >= 80 && <Note tone={pct >= 95 ? "error" : "warn"}>{pct.toFixed(0)} % des Budgets verbraucht.</Note>}
    </>
  );
}
